/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState, useEffect } from 'react';
import { GitBranch, Check, X, RefreshCw } from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import { useToast } from '../../hooks/use-toast';
import { SkillShopService } from '../../services/skillShopService';
import { LinearConfig } from '../../types';
import { LinearTeam } from '@shared/types/responses/linear-import';

interface ConnectionStatus {
  tested: boolean;
  success: boolean;
  error?: string;
}

export function LinearSettings() {
  const { 
    linearConfig, 
    updateLinearConfig, 
    clearLinearConfig,
    isLinearConfigured 
  } = useApp();
  const { toast } = useToast();
  const [skillShopService] = useState(() => new SkillShopService());

  const [formData, setFormData] = useState<LinearConfig>({
    apiKey: '',
    defaultTeamId: null,
    includeCompleted: false,
  });

  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>({ 
    tested: false, 
    success: false 
  });
  const [linearTeams, setLinearTeams] = useState<LinearTeam[]>([]);

  // Load existing config
  useEffect(() => {
    if (linearConfig) {
      setFormData({
        apiKey: linearConfig.apiKey,
        defaultTeamId: linearConfig.defaultTeamId ?? null,
        includeCompleted: linearConfig.includeCompleted ?? false,
        teamIds: linearConfig.teamIds ?? undefined,
      });
    } else {
      setFormData({
        apiKey: '',
        defaultTeamId: null,
        includeCompleted: false,
      });
      setLinearTeams([]);
      setConnectionStatus({ tested: false, success: false });
    }
  }, [linearConfig]);

  const handleInputChange = (field: keyof LinearConfig, value: string | boolean | null | string[]) => {
    setFormData(prev => ({
      ...prev,
      [field]: value as never,
    }));
    setConnectionStatus({ tested: false, success: false });
  };

  const testConnection = async () => {
    if (!formData.apiKey) {
      setConnectionStatus({
        tested: true,
        success: false,
        error: 'Please provide a Linear API key'
      });
      return;
    }

    setIsTestingConnection(true);
    setConnectionStatus({ tested: false, success: false });

    try {
      const result = await skillShopService.testLinearConnection({
        apiKey: formData.apiKey,
        defaultTeamId: formData.defaultTeamId || undefined
      });

      if (result.success) {
        setConnectionStatus({ tested: true, success: true });

        try {
          const teamsResponse = await skillShopService.getLinearTeams({
            apiKey: formData.apiKey,
            defaultTeamId: formData.defaultTeamId || undefined
          });
          if (teamsResponse.success && teamsResponse.data) {
            setLinearTeams(teamsResponse.data.teams || []);
            
            if (!formData.defaultTeamId && teamsResponse.data.teams?.length) {
              setFormData(prev => ({
                ...prev,
                defaultTeamId: teamsResponse.data!.teams[0].id,
              }));
            }
          }
        } catch (teamError: any) {
          console.error('Failed to load Linear teams:', teamError);
        }
      } else {
        setConnectionStatus({
          tested: true,
          success: false,
          error: result.error || 'Connection failed'
        });
      }
    } catch (error: any) {
      setConnectionStatus({
        tested: true,
        success: false,
        error: error.message || 'Connection test failed'
      });
    } finally {
      setIsTestingConnection(false);
    }
  };

  const saveConfiguration = () => {
    if (!formData.apiKey) {
      toast({
        title: 'Missing API key',
        description: 'Provide a Linear API key before saving.',
        variant: 'destructive',
      });
      return;
    }

    updateLinearConfig({
      apiKey: formData.apiKey,
      defaultTeamId: formData.defaultTeamId ?? undefined,
      includeCompleted: formData.includeCompleted ?? false,
    });
    toast({
      title: 'Configuration saved',
      description: 'Your Linear settings have been updated.',
    });
  };

  const clearConfiguration = () => {
    if (confirm('Are you sure you want to clear the Linear configuration?')) {
      clearLinearConfig();
      setFormData({
        apiKey: '',
        defaultTeamId: null,
        includeCompleted: false,
      });
      setConnectionStatus({ tested: false, success: false });
      setLinearTeams([]);
    }
  };

  return (
    <div style={{ 
      marginBottom: '32px',
      padding: '20px',
      background: '#f9f9f9',
      borderRadius: '8px',
      border: '1px solid #ddd'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <GitBranch size={20} style={{ marginRight: '8px', color: '#0066cc' }} />
        <h3 style={{ margin: 0 }}>Linear Integration</h3>
      </div>

      <div className="form-group">
        <label htmlFor="linear-api-key">API Key</label>
        <input
          id="linear-api-key"
          type="password"
          placeholder="lin_api_..."
          value={formData.apiKey}
          onChange={(e) => handleInputChange('apiKey', e.target.value)}
        />
        <small style={{ color: '#666', fontSize: '12px' }}>
          Create a personal API key in your Linear workspace settings under <em>API</em>.
        </small>
      </div>

      <div style={{ marginBottom: '24px' }}>
        <button
          className="btn"
          onClick={testConnection}
          disabled={isTestingConnection || !formData.apiKey}
        >
          {isTestingConnection ? (
            <>
              <RefreshCw size={16} style={{ marginRight: '8px' }} className="spin" />
              Testing Connection...
            </>
          ) : (
            'Test Connection'
          )}
        </button>

        {connectionStatus.tested && (
          <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center' }}>
            {connectionStatus.success ? (
              <>
                <Check size={16} style={{ color: '#28a745', marginRight: '8px' }} />
                <span className="status-badge success">Connection successful</span>
              </>
            ) : (
              <>
                <X size={16} style={{ color: '#dc3545', marginRight: '8px' }} />
                <span className="status-badge error">
                  {connectionStatus.error || 'Connection failed'}
                </span>
              </>
            )}
          </div>
        )}
      </div>

      {linearTeams.length > 0 && (
        <div className="form-group">
          <label htmlFor="linear-default-team">Default Team</label>
          <select
            id="linear-default-team"
            value={formData.defaultTeamId ?? ''}
            onChange={(e) => handleInputChange('defaultTeamId', e.target.value || null)}
          >
            <option value="">Select a team...</option>
            {linearTeams.map(team => (
              <option key={team.id} value={team.id}>
                {team.name} ({team.key})
              </option>
            ))}
          </select>
          <small style={{ color: '#666', fontSize: '12px' }}>
            Choose the team whose issues will be imported by default.
          </small>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
        <input
          id="linear-include-completed"
          type="checkbox"
          checked={formData.includeCompleted ?? false}
          onChange={(e) => handleInputChange('includeCompleted', e.target.checked)}
        />
        <label htmlFor="linear-include-completed" style={{ fontSize: '14px' }}>
          Include completed issues when importing
        </label>
      </div>

      <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
        <button
          className="btn"
          onClick={saveConfiguration}
          disabled={!formData.apiKey}
        >
          Save Linear Configuration
        </button>

        {isLinearConfigured && (
          <button
            className="btn btn-secondary"
            onClick={clearConfiguration}
          >
            Clear Linear Configuration
          </button>
        )}
      </div>
    </div>
  );
}
